import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { X, CheckCircle2, XCircle, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { diffWords } from "diff";

export type DiffSide = {
  readonly label: string;
  readonly sublabel?: string;
  readonly content: string;
  readonly versionId?: string;
  readonly contractId?: string;
  readonly sectionId?: string;
  readonly isActive?: boolean;
  readonly isExtracted?: boolean;
};

export type SectionDiffModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly left: DiffSide;
  readonly right: DiffSide;
  readonly onActivate?: (versionId: string, scope?: { contractId: string; sectionId: string }) => Promise<void>;
  readonly onDeactivate?: (versionId: string, scope?: { contractId: string; sectionId: string }) => Promise<void>;
  readonly onActivateExtracted?: () => Promise<void>;
};

export const SectionDiffModal = ({ open, onClose, left, right, onActivate, onDeactivate, onActivateExtracted }: SectionDiffModalProps) => {
  const changes = useMemo(
    () => diffWords(left.content, right.content),
    [left.content, right.content]
  );

  const hasChanges = changes.some((c) => c.added || c.removed);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleAction = async (versionId: string, action: "activate" | "deactivate") => {
    setActionLoading(versionId);
    try {
      const side = [left, right].find((s) => s.versionId === versionId);
      const scope =
        side?.contractId && side?.sectionId
          ? { contractId: side.contractId, sectionId: side.sectionId }
          : undefined;
      if (action === "activate" && onActivate) await onActivate(versionId, scope);
      if (action === "deactivate" && onDeactivate) await onDeactivate(versionId, scope);
      onClose();
    } catch {
      // errors handled by parent
    } finally {
      setActionLoading(null);
    }
  };

  const [extractedLoading, setExtractedLoading] = useState(false);

  const handleActivateExtractedAction = async () => {
    if (!onActivateExtracted) return;
    setExtractedLoading(true);
    try {
      await onActivateExtracted();
      onClose();
    } catch {
      // errors handled by parent
    } finally {
      setExtractedLoading(false);
    }
  };

  const renderActionButton = (side: DiffSide) => {
    if (side.isExtracted && onActivateExtracted) {
      return (
        <button
          type="button"
          onClick={() => void handleActivateExtractedAction()}
          disabled={extractedLoading}
          className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-primary-300 bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-50"
        >
          {extractedLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          Activate
        </button>
      );
    }
    if (!side.versionId) return null;
    if (side.isActive && onDeactivate) {
      return (
        <button
          type="button"
          onClick={() => void handleAction(side.versionId!, "deactivate")}
          disabled={actionLoading === side.versionId}
          className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
        >
          {actionLoading === side.versionId ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
          Deactivate
        </button>
      );
    }
    if (!side.isActive && onActivate) {
      return (
        <button
          type="button"
          onClick={() => void handleAction(side.versionId!, "activate")}
          disabled={actionLoading === side.versionId}
          className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-primary-300 bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-50"
        >
          {actionLoading === side.versionId ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          Activate
        </button>
      );
    }
    return null;
  };

  const changeIndices = useMemo(
    () => changes.reduce<number[]>((acc, part, i) => {
      if (part.added || part.removed) acc.push(i);
      return acc;
    }, []),
    [changes]
  );
  const totalChanges = changeIndices.length;
  const [activeChangeIdx, setActiveChangeIdx] = useState(-1);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  const scrollToChange = useCallback((idx: number) => {
    if (idx < 0 || idx >= totalChanges) return;
    setActiveChangeIdx(idx);
    const changeIndex = changeIndices[idx];
    const dataAttr = `[data-change-idx="${changeIndex}"]`;
    for (const panel of [leftPanelRef.current, rightPanelRef.current]) {
      const el = panel?.querySelector(dataAttr) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        break;
      }
    }
  }, [totalChanges, changeIndices]);

  const goNextChange = useCallback(() => {
    scrollToChange(activeChangeIdx < totalChanges - 1 ? activeChangeIdx + 1 : 0);
  }, [activeChangeIdx, totalChanges, scrollToChange]);

  const goPrevChange = useCallback(() => {
    scrollToChange(activeChangeIdx > 0 ? activeChangeIdx - 1 : totalChanges - 1);
  }, [activeChangeIdx, totalChanges, scrollToChange]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); goNextChange(); }
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); goPrevChange(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose, goNextChange, goPrevChange]);

  useEffect(() => { if (open) setActiveChangeIdx(-1); }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="diff-modal-title"
    >
      <div className="flex w-full max-w-6xl max-h-[90vh] flex-col rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 id="diff-modal-title" className="text-lg font-semibold text-gray-900">
            Compare Versions
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Column headers */}
        <div className="grid shrink-0 grid-cols-2 border-b border-gray-200">
          <div className="border-r border-gray-200 px-6 py-3 bg-red-50/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">{left.label}</p>
                {left.sublabel && <p className="text-xs text-gray-500">{left.sublabel}</p>}
              </div>
              {renderActionButton(left)}
            </div>
          </div>
          <div className="px-6 py-3 bg-emerald-50/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">{right.label}</p>
                {right.sublabel && <p className="text-xs text-gray-500">{right.sublabel}</p>}
              </div>
              {renderActionButton(right)}
            </div>
          </div>
        </div>

        {/* Diff content */}
        <div className="grid min-h-0 flex-1 grid-cols-2 overflow-hidden">
          {/* Left side (old) */}
          <div ref={leftPanelRef} className="border-r border-gray-200 overflow-auto p-6">
            <div className="text-sm leading-relaxed whitespace-pre-wrap font-sans">
              {changes.map((part, i) => {
                if (part.added) return null;
                if (part.removed) {
                  const isHighlighted = changeIndices[activeChangeIdx] === i;
                  return (
                    <span
                      key={i}
                      data-change-idx={i}
                      className={`rounded-sm px-0.5 ${isHighlighted ? "bg-red-300 text-red-900 ring-2 ring-red-400" : "bg-red-100 text-red-800"}`}
                    >
                      {part.value}
                    </span>
                  );
                }
                return <span key={i}>{part.value}</span>;
              })}
            </div>
          </div>

          {/* Right side (new) */}
          <div ref={rightPanelRef} className="overflow-auto p-6">
            <div className="text-sm leading-relaxed whitespace-pre-wrap font-sans">
              {changes.map((part, i) => {
                if (part.removed) return null;
                if (part.added) {
                  const isHighlighted = changeIndices[activeChangeIdx] === i;
                  return (
                    <span
                      key={i}
                      data-change-idx={i}
                      className={`rounded-sm px-0.5 ${isHighlighted ? "bg-emerald-300 text-emerald-900 ring-2 ring-emerald-400" : "bg-emerald-100 text-emerald-800"}`}
                    >
                      {part.value}
                    </span>
                  );
                }
                return <span key={i}>{part.value}</span>;
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 px-6 py-3">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-red-100 border border-red-200" />
              Removed
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-emerald-100 border border-emerald-200" />
              Added
            </span>
            {!hasChanges && (
              <span className="text-gray-400">No differences found</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {totalChanges > 0 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={goPrevChange}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 transition-colors hover:bg-gray-50"
                  aria-label="Previous change"
                  title="Previous change (↑)"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[4rem] text-center text-xs text-gray-500">
                  {activeChangeIdx >= 0 ? activeChangeIdx + 1 : "—"} / {totalChanges}
                </span>
                <button
                  type="button"
                  onClick={goNextChange}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 transition-colors hover:bg-gray-50"
                  aria-label="Next change"
                  title="Next change (↓)"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Close
              <span className="ml-1.5 text-xs text-gray-400">Esc</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
